import type {
  Article,
  ArticleConnection,
  ArticleEdge,
  Blog,
  BlogConnection,
  Metafield,
} from "@shopify/hydrogen-react/storefront-api-types";
import { query } from "gql-query-builder";
import type IQueryBuilderOptions from "gql-query-builder/build/IQueryBuilderOptions";
import { APP_NAME, articlesFields, pageFields } from "./fields";
import {
  articleMetafield,
  articleThemeMetafield,
  blogVideoMetafield,
  relatedProductsMetafield,
  seasonMetafield,
  thumbnailMetafield,
} from "./metafields/blog-metafields";

/**
 * handle 이용하여 블로그의 아티클 목록 조회
 */
export interface IBlogAllQueryNode extends Article {
  thumbnail?: Metafield;
  video?: Metafield;
  relatedProducts?: Metafield;
  articleProducts?: Metafield;
  articleTheme?: Metafield;
  season?: Metafield;
}

export interface IBlogArticlesEdge extends ArticleEdge {
  node: IBlogAllQueryNode;
}

export interface IBlogAllQuery {
  blog: Blog & {
    articles?: ArticleConnection & { edges: IBlogArticlesEdge[] };
    cursor_list: ArticleConnection;
  };
}

export const blogMetafields: Record<string, IQueryBuilderOptions[]> = {
  tune: [thumbnailMetafield, blogVideoMetafield, articleThemeMetafield],
  yeseyesee: [thumbnailMetafield, blogVideoMetafield],
  thisisneverthat: [],
  khakis: [relatedProductsMetafield],
  sunlove: [relatedProductsMetafield],
  wellbeingexpress: [thumbnailMetafield, seasonMetafield],
};

export const cursorList = {
  operation: { name: "articles", alias: "cursor_list" },
  fields: [
    {
      edges: [
        "cursor",
        { node: [...articlesFields, ...blogMetafields[APP_NAME]] },
      ],
    },
    { pageInfo: pageFields },
  ],
  variables: {
    cursor_query: { type: "String", value: "query", name: "query" },
    cursor_first: { type: "Int", value: "first", name: "first" },
    cursor_sortKey: {
      type: "ArticleSortKeys",
      value: "sortKey",
      name: "sortKey",
    },
  },
};

export const blogAllQuery = query(
  {
    operation: "blog",
    fields: [
      {
        operation: "articles",
        fields: [
          {
            edges: [{ node: [...articlesFields, ...blogMetafields[APP_NAME]] }],
          },
          { pageInfo: pageFields },
        ],
        variables: {
          first: { type: "Int" },
          after: { type: "String" },
          sortKey: { type: "ArticleSortKeys" },
          reverse: { type: "Boolean" },
          query: { type: "String" },
        },
      },
      cursorList,
    ],
    variables: {
      handle: { type: "String" },
    },
  },
  undefined,
  { operationName: "Blog" },
);

/**
 * handle 이용하여 특정 블로그의 특정 아티클 조회
 */
export interface IBlogHandleQuery {
  blog: Blog & {
    cursor_list: ArticleConnection & {
      edges: ArticleEdge & {
        node: IBlogAllQueryNode;
      };
    };
  };
}

export const blogHandleMetafields: Record<string, IQueryBuilderOptions[]> = {
  tune: [thumbnailMetafield, blogVideoMetafield, articleThemeMetafield],
  yeseyesee: [thumbnailMetafield, blogVideoMetafield],
  thisisneverthat: [relatedProductsMetafield],
  khakis: [relatedProductsMetafield],
  sunlove: [relatedProductsMetafield, articleMetafield],
  wellbeingexpress: [relatedProductsMetafield],
};

export const blogHandleQuery = query(
  [
    {
      operation: "blog",
      fields: [
        {
          operation: "articleByHandle",
          fields: [...articlesFields, ...blogHandleMetafields[APP_NAME]],
          variables: { articleHandle: { type: "String!", name: "handle" } },
        },
        cursorList,
      ],
      variables: { handle: { type: "String" } },
    },
  ],
  undefined,
  { operationName: "BlogHandle" },
);

export interface IBlogsQuery {
  blogs: BlogConnection;
}

export const blogsQuery = query(
  {
    operation: "blogs",
    fields: [
      {
        edges: [
          {
            node: [
              "handle",
              {
                operation: "articles",
                fields: [
                  {
                    edges: [
                      {
                        node: [...articlesFields, ...blogMetafields[APP_NAME]],
                      },
                    ],
                  },
                  // 아티클이 더 남았는지는 blog 마다 따로 봐야 한다.
                  // blogs 레벨 pageInfo 는 "blog 가 더 있는지"라 목록에서 쓸 수 없다.
                  { pageInfo: pageFields },
                ],
                // articles 는 connection 이라 first/last 중 하나가 없으면
                // "you must provide one of first or last" 로 실패한다.
                // 바깥 blogs 의 $first / $query 와 겹치지 않게 별도 변수명을 쓴다.
                variables: {
                  articles_first: { type: "Int", name: "first" },
                  articles_sortKey: {
                    type: "ArticleSortKeys",
                    name: "sortKey",
                  },
                  articles_reverse: { type: "Boolean", name: "reverse" },
                  // lookbook 연도 필터처럼 아티클 단위 검색어가 필요할 때 쓴다
                  articles_query: { type: "String", name: "query" },
                },
              },
              // cursorList,
            ],
          },
        ],
      },
      { pageInfo: pageFields },
    ],
    variables: {
      first: { type: "Int" },
      last: { type: "Int" },
      after: { type: "String" },
      sortKey: { type: "BlogSortKeys" },
      reverse: { type: "Boolean" },
      query: { type: "String" },
    },
  },
  undefined,
  { operationName: "Blogs" },
);
